import { useState } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { RiUserFill, RiUserLine } from 'react-icons/ri'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { FreshnessTag, IProfileAttributeSimilarMatch } from '@brushfire/types'
import { ApiError } from '@/api/http/types'
import { suggestProfileAttributeName } from '@brushfire/utils/profile-freshness'
import { fetchQuestionBankEntryById } from '@/features/question-bank/api/question-bank-queries'
import { questionBankSaveBookmarkButtonSx } from '@/shared/lib/libraryCardHover'
import { ConfirmationModalIcon } from '@/shared/components/confirmation/ConfirmationModalIcon'
import {
  confirmationBodySx,
  confirmationHeadlineSx,
  confirmationPrimaryButtonSx,
  confirmationSecondaryButtonSx,
} from '@/shared/components/confirmation/confirmationModalStyles'
import { builderQuestionToBankPayload } from '@/features/question-bank/lib/questionBankMappers'
import type { IQuestionBankEntryDetail } from '@brushfire/types'
import type { BuilderQuestion } from './types'
import {
  FRESHNESS_CHOICES,
  createProfileAttribute,
  findSimilarProfileAttribute,
  isProfileBuilderType,
  listProfileAttributes,
  updateProfileAttribute,
  type ProfileLink,
} from './profile-attributes.api'

type Mode = 'confirm' | 'similar' | 'update' | null

function profileSaveError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 409 && err.message.trim()) {
    return err.message
  }
  return fallback
}

const fieldLabelSx = {
  display: 'block',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '20px',
  color: '#0f172a',
  mb: '8px',
} as const

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#fff',
    minHeight: 46,
  },
  '& .MuiOutlinedInput-input': {
    color: '#04183f',
    fontSize: '14px',
  },
} as const

type Props = Readonly<{
  question: BuilderQuestion
  onChange: (patch: Partial<BuilderQuestion>) => void
  onUseBankEntry: (entry: IQuestionBankEntryDetail, link: ProfileLink) => void
}>

export function ProfileUpdateQuestionButton({ question, onChange, onUseBankEntry }: Props) {
  const [mode, setMode] = useState<Mode>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [match, setMatch] = useState<IProfileAttributeSimilarMatch | null>(null)
  const [name, setName] = useState('')
  const [editingAttributeId, setEditingAttributeId] = useState<string | null>(null)
  const [freshnessTag, setFreshnessTag] = useState<FreshnessTag>('PERM')
  const active = question.isProfileUpdate === true
  const profileAllowed = isProfileBuilderType(question.type)

  const close = () => {
    setMode(null)
    setError(null)
    setBusy(false)
    setEditingAttributeId(null)
  }

  const beginCreate = () => {
    onChange({ isProfileUpdate: true, fromQuestionBank: true })
    setEditingAttributeId(null)
    setName(suggestProfileAttributeName(question.questionText))
    setFreshnessTag('PERM')
    setMode('confirm')
  }

  const beginUpdate = async (attributeId: string) => {
    setEditingAttributeId(attributeId)
    setBusy(true)
    setError(null)
    try {
      const rows = await listProfileAttributes()
      const current = rows.find((row) => row.id.toLowerCase() === attributeId.toLowerCase())
      if (!current) {
        setName('')
        setError('Could not load this profile attribute.')
      } else {
        setName(current.name)
      }
    } catch (err) {
      setName('')
      setError(profileSaveError(err, 'Could not load this profile attribute.'))
    } finally {
      setBusy(false)
    }
    setMode('update')
  }

  const attributeIdFromBankEntry = (entry: IQuestionBankEntryDetail): string | null => {
    const linked = entry.profileAttributeId?.trim()
    if (!linked) return null
    onChange({
      isProfileUpdate: true,
      fromQuestionBank: true,
      profileAttributeId: linked,
      questionBankEntryId: entry.id,
    })
    return linked
  }

  const open = () => {
    if (!profileAllowed) return
    setError(null)
    const linkedId = question.profileAttributeId?.trim()
    if (linkedId) {
      void beginUpdate(linkedId)
      return
    }
    const bankEntryId = question.questionBankEntryId?.trim()
    if (!bankEntryId) {
      beginCreate()
      return
    }
    void (async () => {
      setBusy(true)
      try {
        const entry = await fetchQuestionBankEntryById(bankEntryId)
        const linked = attributeIdFromBankEntry(entry)
        if (linked) {
          await beginUpdate(linked)
          return
        }
      } catch {
        if (question.isProfileUpdate === true) {
          setEditingAttributeId(null)
          setName('')
          setError('Could not load this profile question.')
          setMode('update')
          setBusy(false)
          return
        }
      }
      setBusy(false)
      beginCreate()
    })()
  }

  const cancelConfirm = () => {
    onChange({
      isProfileUpdate: false,
      fromQuestionBank: Boolean(question.questionBankEntryId),
    })
    close()
  }

  const continueConfirm = async () => {
    const prompt = question.questionText.trim()
    const trimmed = name.trim()
    if (!prompt) {
      setError('Add question text before marking this as a profile update.')
      return
    }
    if (!trimmed) {
      setError('Attribute name is required.')
      return
    }
    setBusy(true)
    setError(null)
    let found: Awaited<ReturnType<typeof findSimilarProfileAttribute>> = null
    try {
      found = await findSimilarProfileAttribute({
        prompt,
        attributeName: trimmed,
      })
    } catch {
      found = null
    } finally {
      setBusy(false)
    }
    if (found) {
      setMatch(found)
      setMode('similar')
      return
    }

    setBusy(true)
    try {
      const payload = builderQuestionToBankPayload(question)
      const created = await createProfileAttribute({
        name: trimmed,
        freshnessTag,
        prompt: payload.prompt,
        type: payload.type,
        question: payload.question,
      })
      onChange({
        isProfileUpdate: true,
        fromQuestionBank: true,
        profileAttributeId: created.id,
        questionBankEntryId: created.questionBankEntryId ?? undefined,
        freshnessTag: created.freshnessTag,
        systemKey: created.systemKey,
      })
      close()
    } catch (err) {
      setError(profileSaveError(err, 'Could not save this profile attribute. Please try again.'))
    } finally {
      setBusy(false)
    }
  }

  const useMatch = async () => {
    if (!match) return
    setBusy(true)
    setError(null)
    try {
      const entry = await fetchQuestionBankEntryById(match.questionBankEntryId)
      onUseBankEntry(entry, {
        profileAttributeId: match.profileAttributeId,
        freshnessTag: match.freshnessTag,
        systemKey: match.systemKey,
        questionBankEntryId: match.questionBankEntryId,
      })
      close()
    } catch {
      setError('Could not load the existing profile question.')
      setBusy(false)
    }
  }

  const saveUpdate = async () => {
    const attributeId = editingAttributeId ?? question.profileAttributeId?.trim()
    if (!attributeId) return
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Attribute name is required.')
      return
    }
    const payload = builderQuestionToBankPayload(question)
    setBusy(true)
    setError(null)
    try {
      const updated = await updateProfileAttribute(attributeId, {
        name: trimmed,
        prompt: payload.prompt,
        type: payload.type,
        question: payload.question,
      })
      onChange({
        isProfileUpdate: true,
        freshnessTag: updated.freshnessTag,
        systemKey: updated.systemKey,
        questionBankEntryId: updated.questionBankEntryId ?? question.questionBankEntryId,
      })
      close()
    } catch (err) {
      setError(profileSaveError(err, 'Could not update this profile question.'))
      setBusy(false)
    }
  }

  const button = (
    <IconButton
      data-testid="question-profile-update"
      aria-label="Profile update question"
      aria-pressed={active}
      disabled={!profileAllowed}
      onClick={open}
      sx={{
        ...questionBankSaveBookmarkButtonSx,
        color: active ? '#008cd0' : '#64748b',
        ...(active ? { borderColor: '#008cd0' } : {}),
      }}
    >
      {active ? (
        <RiUserFill size={18} aria-hidden data-testid="question-profile-update-filled" />
      ) : (
        <RiUserLine size={18} aria-hidden data-testid="question-profile-update-outline" />
      )}
    </IconButton>
  )

  return (
    <>
      {profileAllowed ? (
        button
      ) : (
        <Tooltip title="Profile updates are available for choice and short-text questions.">
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {button}
          </Box>
        </Tooltip>
      )}

      <Dialog
        open={mode !== null}
        onClose={mode === 'confirm' ? cancelConfirm : close}
        data-testid={
          mode === 'similar'
            ? 'profile-update-similar-dialog'
            : mode === 'update'
              ? 'profile-update-edit-dialog'
              : 'profile-update-confirm-dialog'
        }
        PaperProps={{
          elevation: 0,
          sx: {
            width: '100%',
            maxWidth: mode === 'confirm' ? 646 : 448,
            borderRadius: '10px',
            boxShadow: '0px 20px 12.5px rgba(0, 0, 0, 0.1), 0px 8px 5px rgba(0, 0, 0, 0.1)',
            m: 2,
            maxHeight: 'calc(100vh - 32px)',
          },
        }}
      >
        {mode === 'confirm' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', pt: '32px', px: '24px', pb: '24px' }}>
            <Box
              aria-hidden
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#f1faff',
                color: '#008cd0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: '24px',
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography component="h2" sx={{ ...confirmationHeadlineSx, mb: '12px' }}>
              Are you sure you want to update this question as a Profile Update Question?
            </Typography>
            <Typography sx={{ ...confirmationBodySx, textAlign: 'center', maxWidth: 388, mx: 'auto', mb: '20px' }}>
              Responses to this question will be saved to the responder&apos;s profile and can be used for future audience targeting.
            </Typography>
            <Box
              sx={{
                bgcolor: '#f7fcff',
                border: '1px solid #d1f0ff',
                borderRadius: '8px',
                p: '19px 24px 24px',
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '16px', lineHeight: '20px', color: '#0f172a', mb: '16px' }}>
                Profile Attribute
              </Typography>
              <Typography component="label" htmlFor="profile-update-attribute-name" sx={fieldLabelSx}>
                Profile Attribute Name
              </Typography>
              <TextField
                id="profile-update-attribute-name"
                data-testid="profile-update-attribute-name"
                fullWidth
                value={name}
                onChange={(event) => setName(event.target.value)}
                sx={fieldSx}
              />
              <Typography sx={{ fontSize: '12px', lineHeight: '20px', color: '#9ca3af', mt: '8px', mb: '16px' }}>
                This will be the name of the profile attribute used for audience targeting
              </Typography>
              <Typography component="label" id="profile-update-freshness-label" sx={fieldLabelSx}>
                Freshness/ Refresh Frequency
              </Typography>
              <TextField
                select
                fullWidth
                data-testid="profile-update-freshness-tag"
                value={freshnessTag}
                onChange={(event) => setFreshnessTag(event.target.value as FreshnessTag)}
                SelectProps={{ labelId: 'profile-update-freshness-label' }}
                sx={fieldSx}
              >
                {FRESHNESS_CHOICES.map((choice) => (
                  <MenuItem key={choice.tag} value={choice.tag}>
                    {choice.label}
                  </MenuItem>
                ))}
              </TextField>
              <Box
                sx={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                  bgcolor: '#cfeeff',
                  borderRadius: '8px',
                  p: '10px',
                  mt: '12px',
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18, color: '#008cd0', mt: '1px', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: '#6b7a90' }}>
                  Determines when this profile information becomes eligible to be asked again. Reaching the refresh date does not invalidate or delete the existing answer.
                </Typography>
              </Box>
              {error ? (
                <Typography role="alert" sx={{ color: 'error.main', mt: '12px', fontSize: '13px' }}>
                  {error}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', mt: '22px' }}>
              <Button
                variant="outlined"
                data-testid="profile-update-confirm-cancel"
                onClick={cancelConfirm}
                sx={confirmationSecondaryButtonSx}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                data-testid="profile-update-confirm-continue"
                disabled={busy}
                onClick={() => {
                  void continueConfirm()
                }}
                sx={{ ...confirmationPrimaryButtonSx, minWidth: 141 }}
              >
                {busy ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
              </Button>
            </Box>
          </Box>
        ) : null}
        {mode === 'similar' ? (
          <>
            <DialogTitle>Similar Profile Update Question Found</DialogTitle>
            <DialogContent>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>{match?.attributeName}</Typography>
              <Typography sx={{ fontSize: '14px', mb: 1 }}>{match?.prompt}</Typography>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                Options: {match?.options.join(', ') || 'Free text'}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                Freshness: {match?.freshnessTag}
              </Typography>
              {error ? (
                <Typography sx={{ color: 'error.main', mt: 1, fontSize: '13px' }}>{error}</Typography>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button data-testid="profile-update-similar-cancel" onClick={cancelConfirm}>
                Cancel
              </Button>
              <Button
                data-testid="profile-update-similar-use"
                variant="contained"
                disabled={busy}
                onClick={() => {
                  void useMatch()
                }}
              >
                {busy ? <CircularProgress size={20} color="inherit" /> : 'Use this question'}
              </Button>
            </DialogActions>
          </>
        ) : null}
        {mode === 'update' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: '32px' }}>
            <ConfirmationModalIcon variant="edit" />
            <Box
              sx={{
                width: '100%',
                px: '24px',
                pt: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Typography component="h2" sx={confirmationHeadlineSx}>
                Update profile question
              </Typography>
              <Typography sx={{ ...confirmationBodySx, textAlign: 'center', maxWidth: 388 }}>
                Question text and options are saved. Freshness stays {question.freshnessTag ?? 'unchanged'}.
              </Typography>
              <Box sx={{ width: '100%', maxWidth: 388 }}>
                <Typography component="label" htmlFor="profile-update-edit-name" sx={fieldLabelSx}>
                  Profile Attribute Name
                </Typography>
                <TextField
                  id="profile-update-edit-name"
                  data-testid="profile-update-edit-name"
                  fullWidth
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  sx={fieldSx}
                />
              </Box>
              {error ? (
                <Typography role="alert" sx={{ color: 'error.main', fontSize: '13px', textAlign: 'center' }}>
                  {error}
                </Typography>
              ) : null}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                width: '100%',
                px: 3,
                py: 3,
              }}
            >
              <Button variant="outlined" onClick={close} sx={confirmationSecondaryButtonSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                data-testid="profile-update-edit-save"
                disabled={busy}
                onClick={() => {
                  void saveUpdate()
                }}
                sx={confirmationPrimaryButtonSx}
              >
                {busy ? <CircularProgress size={20} color="inherit" /> : 'Update'}
              </Button>
            </Box>
          </Box>
        ) : null}
      </Dialog>
    </>
  )
}
